import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
    DollarSign, BrainCircuit, Download, FileSpreadsheet, 
    FileText, CheckCircle, Loader2, BarChart3, TrendingUp,
    PieChart, Calendar, AlertTriangle, ShieldCheck, Wallet,
    ArrowRightLeft, Target, RefreshCw
} from 'lucide-react';

// Shared UI
import { 
    AppButton, BlackboxLoaderIcon 
} from '../../components/SharedUI';

// Services
import { generateBudgetPlanLLM } from '../../services/aiService';

// --- STYLED COMPONENTS ---

const BudgetInput = ({ label, value, onChange, prefix, onSync, syncLabel }) => (
    <div className="group">
        <div className="flex justify-between items-center mb-1.5">
            <label className="block text-[10px] font-bold text-emerald-500/80 uppercase tracking-widest group-focus-within:text-emerald-400 transition-colors">
                {label}
            </label>
            {onSync && (
                <button 
                    onClick={onSync}
                    className="text-[10px] font-bold text-emerald-600 hover:text-emerald-400 flex items-center gap-1 transition-colors uppercase tracking-wider"
                >
                    <ArrowRightLeft size={10} /> {syncLabel}
                </button>
            )}
        </div>
        <div className="relative">
            {prefix && (
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 font-bold text-lg pointer-events-none">
                    {prefix}
                </div>
            )}
            <input 
                type="number"
                value={value}
                onChange={onChange}
                className={`w-full bg-zinc-900/80 border border-zinc-800 text-white font-mono text-lg px-4 py-4 rounded-xl focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500/50 transition-all placeholder:text-zinc-700 shadow-inner ${prefix ? 'pl-10' : ''}`}
                placeholder="0.00"
            />
        </div>
    </div>
);

const AllocationBar = ({ percent, colorClass = "bg-emerald-500" }) => (
    <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div 
            className={`h-full rounded-full ${colorClass} transition-all duration-1000 ease-out`} 
            style={{ width: `${Math.min(percent, 100)}%` }}
        />
    </div>
);

// --- PREMIUM OVERLAY ---
const PremiumBudgetOverlay = ({ isVisible, currentStepIndex }) => {
    if (!isVisible) return null;

    const steps = [
        "Ingesting Audience Psychographics...",
        "Aligning with Revenue Forecasts...",
        "Optimizing Media Mix (Digital vs OOH)...",
        "Modeling Risk & Contingency Buffers...",
        "Synthesizing Cash-Flow Waveform...",
        "Finalizing Financial Playbook..."
    ];

    const progress = Math.min(((currentStepIndex + 1) / steps.length) * 100, 100);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/90 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-lg p-8 relative">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className="absolute inset-0 bg-emerald-500 blur-2xl opacity-20 animate-pulse rounded-full"></div>
                        <Wallet className="w-20 h-20 text-white relative z-10 animate-pulse" />
                    </div>
                </div>

                <div className="w-full h-1 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-emerald-600 to-teal-400 transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                <div className="space-y-3 font-mono text-xs">
                    {steps.map((step, index) => {
                        let statusIcon;
                        let statusColor;
                        
                        if (index < currentStepIndex) {
                            statusIcon = <CheckCircle size={14} />;
                            statusColor = "text-emerald-500 opacity-50";
                        } else if (index === currentStepIndex) {
                            statusIcon = <Loader2 size={14} className="animate-spin" />;
                            statusColor = "text-white font-bold";
                        } else {
                            statusIcon = <div className="w-3 h-3 rounded-full border border-zinc-700" />;
                            statusColor = "text-zinc-600";
                        }

                        return (
                            <div key={index} className={`flex items-center gap-3 transition-all duration-300 ${statusColor}`}>
                                <div className="w-5 flex justify-center shrink-0">{statusIcon}</div>
                                <span>{step}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- VIEW COMPONENTS ---

const SnapshotView = ({ budgetPlan, roiAnalysis }) => (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
        
        {/* ROI Header (If Data Exists) */}
        {roiAnalysis && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-emerald-900/30 to-black border border-emerald-500/30 p-5 rounded-2xl">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                            <TrendingUp size={16} className="text-emerald-400" />
                        </div>
                        <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Projected Efficiency</span>
                    </div>
                    <p className="text-3xl font-mono font-bold text-white mb-1">
                        {roiAnalysis.roi}x ROI
                    </p>
                    <p className="text-xs text-zinc-400">
                        Based on realistic box office target of <span className="text-white">{roiAnalysis.revenue}</span>
                    </p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-5 rounded-2xl flex flex-col justify-center">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2">Budget Health</p>
                    <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${roiAnalysis.isHealthy ? 'bg-green-500 shadow-[0_0_10px_#22c55e]' : 'bg-amber-500'}`} />
                        <span className="text-zinc-200 font-medium text-sm">
                            {roiAnalysis.isHealthy ? "Optimal Spend Level" : "Review Allocation"}
                        </span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2 leading-tight">
                        {roiAnalysis.isHealthy 
                            ? "Marketing spend aligns with standard 20-30% P&A ratios for this revenue target."
                            : "Budget may be insufficient to hit revenue targets. Consider increasing P&A."}
                    </p>
                </div>
            </div>
        )}

        <div className="bg-zinc-900/50 p-6 rounded-2xl border border-zinc-800 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <BrainCircuit size={100} />
            </div>
            <h4 className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-4">Strategic Rationale</h4>
            <p className="text-white text-lg font-light leading-relaxed relative z-10">
                "{budgetPlan.executive_snapshot?.rationale}"
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {budgetPlan.executive_snapshot?.kpis.map((kpi, i) => (
                <div key={i} className="bg-zinc-950 border border-zinc-800 p-5 rounded-xl flex flex-col justify-between hover:border-emerald-500/30 transition-colors group">
                    <div className="w-8 h-8 rounded-lg bg-emerald-900/20 flex items-center justify-center text-emerald-500 mb-3 group-hover:scale-110 transition-transform">
                        <Target size={16} />
                    </div>
                    <p className="text-sm font-medium text-zinc-300">{kpi}</p>
                </div>
            ))}
        </div>
    </div>
);

const AllocationView = ({ budgetPlan, formatCurrency }) => {
    // Helper to pick color based on bucket name
    const getBarColor = (bucketName) => {
        const n = bucketName.toLowerCase();
        if (n.includes('digital') || n.includes('social')) return 'bg-blue-500';
        if (n.includes('pr') || n.includes('events')) return 'bg-purple-500';
        if (n.includes('creative') || n.includes('assets')) return 'bg-pink-500';
        return 'bg-emerald-500';
    };

    return (
        <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/30">
                <table className="w-full text-left text-sm">
                    <thead className="bg-zinc-900 text-xs uppercase text-zinc-500 font-bold tracking-wider">
                        <tr>
                            <th className="px-6 py-4">Expense Bucket</th>
                            <th className="px-6 py-4 w-40">Mix %</th>
                            <th className="px-6 py-4 text-right">Allocation</th>
                            <th className="px-6 py-4 hidden md:table-cell">Strategy</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                        {budgetPlan.budget_allocation_table?.map((item, i) => (
                            <tr key={i} className="hover:bg-zinc-800/50 transition-colors">
                                <td className="px-6 py-4 font-bold text-white">{item.bucket}</td>
                                <td className="px-6 py-4">
                                    <div className="flex flex-col gap-1.5">
                                        <div className="flex justify-between text-[10px] font-mono text-zinc-400">
                                            <span>{item.percent_of_budget}%</span>
                                        </div>
                                        <AllocationBar percent={item.percent_of_budget} colorClass={getBarColor(item.bucket)} />
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-right font-mono text-emerald-400 font-bold text-lg">
                                    {formatCurrency(item.dollar_amount)}
                                </td>
                                <td className="px-6 py-4 text-zinc-500 text-xs hidden md:table-cell max-w-xs truncate">
                                    {item.notes}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const TimelineView = ({ budgetPlan }) => (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pl-4">
        <div className="space-y-8 relative border-l-2 border-zinc-800 ml-3 my-4">
            {budgetPlan.phased_timeline?.map((phase, idx) => (
                <div key={idx} className="relative pl-8">
                    {/* Timeline Node */}
                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-zinc-900 border-2 border-emerald-500 shadow-[0_0_10px_#10b981]" />
                    
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors group">
                        <div className="flex justify-between items-start mb-3">
                            <h4 className="text-sm font-bold text-white uppercase tracking-widest flex items-center gap-2">
                                <Calendar size={14} className="text-emerald-500" />
                                {phase.phase}
                            </h4>
                        </div>
                        
                        <div className="space-y-2">
                            {phase.activities.map((activity, i) => (
                                <div key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                                    <div className="w-1.5 h-1.5 rounded-full bg-zinc-600 group-hover:bg-emerald-500 transition-colors" />
                                    {activity}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const RiskView = ({ budgetPlan }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-right-4 duration-500">
        <div className="bg-zinc-900/50 border border-amber-900/30 p-6 rounded-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <BarChart3 size={80} className="text-amber-500" />
            </div>
            <h4 className="text-amber-500 font-bold uppercase tracking-widest text-xs mb-4 flex items-center gap-2">
                <TrendingUp size={14} /> Flex-Budget Strategy
            </h4>
            <p className="text-zinc-300 text-sm leading-relaxed border-l-2 border-amber-500/20 pl-4">
                "{budgetPlan.risk_and_flex_budget?.flex_lines}"
            </p>
        </div>

        <div className="bg-zinc-900/50 border border-red-900/30 p-6 rounded-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                <ShieldCheck size={80} className="text-red-500" />
            </div>
            <h4 className="text-red-400 font-bold uppercase tracking-widest text-xs mb-4 flex items-center gap-2">
                <AlertTriangle size={14} /> Contingency Triggers
            </h4>
            <p className="text-zinc-300 text-sm leading-relaxed border-l-2 border-red-500/20 pl-4">
                "{budgetPlan.risk_and_flex_budget?.trigger_kpis}"
            </p>
        </div>
    </div>
);

// --- MAIN COMPONENT ---

const BudgetPlannerTab = ({ filmData, setNotification, saveFilmData, credits, deductCredit }) => {
    const [totalBudget, setTotalBudget] = useState('1000000');
    const [loading, setLoading] = useState(false);
    const [loadingStepIndex, setLoadingStepIndex] = useState(0);
    const [activeReportTab, setActiveReportTab] = useState('snapshot');
    const resultsRef = useRef(null);

    const budgetPlan = useMemo(() => filmData.budget_plan || null, [filmData.budget_plan]);

    // Data Sources
    const latestAudienceAnalysis = useMemo(() => {
        if (!filmData.ai_analyses || filmData.ai_analyses.length === 0) return null;
        return filmData.ai_analyses[filmData.ai_analyses.length - 1].analysis;
    }, [filmData.ai_analyses]);

    const revenueMarketingBudget = useMemo(() => {
        return filmData.revenue_forecast?.inputs?.marketingSpend || null;
    }, [filmData.revenue_forecast]);

    // Sync budget on load if not set, prioritize Revenue Forecast input
    useEffect(() => {
        if (revenueMarketingBudget && !budgetPlan) {
            setTotalBudget(revenueMarketingBudget);
        }
    }, [revenueMarketingBudget, budgetPlan]);

    // Calculate ROI metrics if we have revenue data
    const roiAnalysis = useMemo(() => {
        if (!budgetPlan || !filmData.revenue_forecast?.data?.scenarios?.realistic) return null;
        
        const realisticGross = filmData.revenue_forecast.data.scenarios.realistic.domestic_box_office + 
                               filmData.revenue_forecast.data.scenarios.realistic.international_box_office;
        
        const budgetVal = parseInt(totalBudget) || 1;
        const roi = (realisticGross / budgetVal).toFixed(1);
        
        // Simple heuristic: If marketing is < 10% or > 50% of gross, flag it
        const ratio = budgetVal / realisticGross;
        const isHealthy = ratio >= 0.10 && ratio <= 0.40;

        return {
            revenue: realisticGross.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }),
            roi,
            isHealthy
        };
    }, [budgetPlan, filmData.revenue_forecast, totalBudget]);

    // --- PROGRESS SIMULATION ---
    useEffect(() => {
        let interval;
        if (loading) {
            setLoadingStepIndex(0);
            interval = setInterval(() => {
                setLoadingStepIndex(prev => (prev < 5 ? prev + 1 : prev));
            }, 1800);
        }
        return () => clearInterval(interval);
    }, [loading]);

    const handleSync = () => {
        if (revenueMarketingBudget) {
            setTotalBudget(revenueMarketingBudget);
            setNotification({ type: 'success', message: 'Synced with Revenue Forecast budget.' });
        } else {
            setNotification({ type: 'info', message: 'No revenue forecast data found.' });
        }
    };

    const handleGeneratePlan = async () => {
        if (credits < 3) {
            setNotification({ type: 'error', message: 'Insufficient credits. Requires 3 credits.' });
            return;
        }

        if (!latestAudienceAnalysis) {
            setNotification({ type: 'error', message: 'Run Audience Analysis first.' });
            return;
        }

        setLoading(true);
        try {
            const result = await generateBudgetPlanLLM(totalBudget, filmData, latestAudienceAnalysis);
            
            // Allow progress bar to finish visuals
            setLoadingStepIndex(5);
            await new Promise(r => setTimeout(r, 800));

            await saveFilmData({ ...filmData, budget_plan: result });
            await deductCredit(3); 
            setNotification({ type: 'success', message: 'Budget architecture generated.' });
            setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        } catch (error) {
            setNotification({ type: 'error', message: `Failed to generate budget: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });

    // CSV Export
    const handleExportCSV = () => {
        try {
            if (!budgetPlan) return;
            let csvContent = "data:text/csv;charset=utf-8,Bucket,Allocation (%),Amount ($),Notes\n";
            budgetPlan.budget_allocation_table?.forEach(item => {
                const cleanNotes = item.notes?.replace(/"/g, '""') || "";
                csvContent += `"${item.bucket}",${item.percent_of_budget},${item.dollar_amount},"${cleanNotes}"\n`;
            });
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `${filmData.title || 'Film'}_Marketing_Budget.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setNotification({ type: 'success', message: 'Budget CSV downloaded.' });
        } catch (error) {
            setNotification({ type: 'error', message: 'Failed to export CSV.' });
        }
    };

    // Premium PDF Export
    const handleExportPDF = () => {
        try {
            if (!window.jspdf || !budgetPlan) {
                setNotification({ type: 'error', message: 'PDF engine not ready.' });
                return;
            }
            
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 50;
            let y = margin;

            // --- STUDIO COLORS ---
            const C = {
                EMERALD: [16, 185, 129],
                DARK: [24, 24, 27],
                GRAY: [113, 113, 122],
                BG_SHADE: [248, 250, 252],
                LINE: [226, 232, 240]
            };

            const checkPageBreak = (needed) => {
                if (y + needed > pageHeight - margin) {
                    doc.addPage();
                    y = margin;
                    return true;
                }
                return false;
            };

            const drawHeader = (title) => {
                checkPageBreak(60);
                y += 20;
                doc.setFillColor(...C.EMERALD);
                doc.rect(margin, y, 4, 18, 'F');
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.setTextColor(...C.DARK);
                doc.text(title.toUpperCase(), margin + 12, y + 14);
                y += 35;
            };

            // 1. COVER HEADER
            doc.setFillColor(...C.DARK);
            doc.rect(0, 0, pageWidth, 120, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(24);
            doc.setFont("helvetica", "bold");
            doc.text("MARKETING BUDGET STRATEGY", margin, 60);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(180, 180, 180);
            doc.text(`PROJECT: ${filmData.title?.toUpperCase() || "UNTITLED"}`, margin, 85);

            // Total Badge
            const totalText = `TOTAL ALLOCATION: ${formatCurrency(parseInt(totalBudget))}`;
            doc.setFont("courier", "bold");
            doc.setFontSize(9);
            const badgeWidth = doc.getTextWidth(totalText) + 30;
            doc.setFillColor(...C.EMERALD);
            doc.roundedRect(pageWidth - margin - badgeWidth, 70, badgeWidth, 25, 3, 3, 'F');
            doc.setTextColor(255, 255, 255);
            doc.text(totalText, pageWidth - margin - badgeWidth + 15, 86);
            
            y = 160;

            // 2. EXECUTIVE SUMMARY
            drawHeader("Executive Snapshot");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            doc.setTextColor(...C.DARK);
            const summary = doc.splitTextToSize(budgetPlan.executive_snapshot?.rationale || "", pageWidth - (margin * 2));
            doc.text(summary, margin, y);
            y += (summary.length * 15) + 20;

            // 3. ALLOCATION TABLE
            drawHeader("Budget Allocation Table");
            doc.setFillColor(...C.BG_SHADE);
            doc.rect(margin, y, pageWidth - (margin * 2), 25, 'F');
            doc.setDrawColor(...C.LINE);
            doc.line(margin, y + 25, pageWidth - margin, y + 25);
            
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...C.GRAY);
            doc.text("EXPENSE BUCKET", margin + 10, y + 16);
            doc.text("MIX %", margin + 200, y + 16);
            doc.text("AMOUNT (USD)", pageWidth - margin - 10, y + 16, { align: 'right' });
            y += 25;

            budgetPlan.budget_allocation_table?.forEach((item) => {
                checkPageBreak(30);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(...C.DARK);
                doc.text(item.bucket, margin + 10, y + 18);
                
                doc.setFont("helvetica", "normal");
                doc.setTextColor(...C.GRAY);
                doc.text(`${item.percent_of_budget}%`, margin + 200, y + 18);
                
                doc.setFont("courier", "bold");
                doc.setTextColor(...C.EMERALD);
                doc.text(formatCurrency(item.dollar_amount), pageWidth - margin - 10, y + 18, { align: 'right' });
                
                y += 28;
                doc.setDrawColor(...C.LINE);
                doc.line(margin, y, pageWidth - margin, y);
            });
            y += 30;

            // 4. TIMELINE
            drawHeader("Phased Cash-Flow");
            budgetPlan.phased_timeline?.forEach((phase) => {
                const content = phase.activities.join(" | ");
                const lines = doc.splitTextToSize(content, pageWidth - (margin * 2) - 30);
                const h = (lines.length * 14) + 40;
                checkPageBreak(h);
                
                doc.setDrawColor(...C.LINE);
                doc.line(margin + 5, y, margin + 5, y + h);
                doc.setFillColor(...C.EMERALD);
                doc.circle(margin + 5, y + 8, 3, 'F');

                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.setTextColor(...C.DARK);
                doc.text(phase.phase, margin + 20, y + 12);
                
                y += 20;
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.setTextColor(...C.GRAY);
                doc.text(lines, margin + 20, y);
                y += (lines.length * 14) + 20;
            });

            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(...C.GRAY);
                doc.text(`BLACKBOX Financial Intelligence | Confidential | Page ${i}`, pageWidth / 2, pageHeight - 30, { align: 'center' });
            }

            doc.save(`${filmData.title?.replace(/\s+/g, '_')}_Budget.pdf`);
            setNotification({ type: 'success', message: 'Budget PDF exported.' });

        } catch (error) {
            console.error("PDF Error:", error);
            setNotification({ type: 'error', message: 'Export failed.' });
        }
    };

    return(
        <div className="h-full grid grid-cols-1 lg:grid-cols-12 gap-8">
            <PremiumBudgetOverlay isVisible={loading} currentStepIndex={loadingStepIndex} />

            {/* --- LEFT COLUMN: INPUTS --- */}
            <div className="lg:col-span-4 flex flex-col gap-6 h-full overflow-y-auto pr-2 pb-32 custom-scrollbar">
                <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 blur-[50px] rounded-full pointer-events-none" />
                    
                    <div className="flex items-center gap-3 mb-8 relative z-10">
                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                            <DollarSign size={16} className="text-emerald-400" />
                        </div>
                        <h3 className="text-lg font-bold text-white font-poppins tracking-tight">Budget Parameters</h3>
                    </div>

                    <div className="space-y-6 relative z-10">
                        <BudgetInput 
                            label="Total Marketing Capital" 
                            prefix="$" 
                            value={totalBudget} 
                            onChange={e => setTotalBudget(e.target.value)}
                            onSync={revenueMarketingBudget ? handleSync : null}
                            syncLabel="Sync from Revenue Model"
                        />
                        
                        <div className={`p-4 rounded-xl border ${latestAudienceAnalysis ? 'bg-emerald-900/10 border-emerald-500/30' : 'bg-amber-900/10 border-amber-500/30'}`}>
                            <div className="flex items-center gap-3 mb-2">
                                <BrainCircuit size={16} className={latestAudienceAnalysis ? "text-emerald-400" : "text-amber-400"} />
                                <span className={`text-xs font-bold uppercase tracking-wider ${latestAudienceAnalysis ? "text-emerald-300" : "text-amber-300"}`}>
                                    Audience Intelligence
                                </span>
                            </div>
                            <p className="text-xs text-zinc-400 leading-relaxed">
                                {latestAudienceAnalysis 
                                    ? "Audience data synced. Allocation strategy will be optimized for your target demographics." 
                                    : "No audience data found. Run Audience Genius first for data-driven allocation."}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Ignition Button */}
                <div className="sticky bottom-0 z-10 pt-4">
                    <button 
                        onClick={handleGeneratePlan} 
                        disabled={loading || !latestAudienceAnalysis} 
                        className="group relative w-full py-4 bg-emerald-600 text-white font-bold rounded-xl overflow-hidden transition-all hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_40px_rgba(16,185,129,0.4)] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-green-400 to-emerald-500 opacity-20 group-hover:opacity-40 transition-opacity animate-[pulse_3s_infinite]" />
                        <span className="relative z-10 flex items-center justify-center gap-2 uppercase tracking-widest text-sm font-black">
                            <BrainCircuit size={18} className={loading ? "animate-pulse" : "fill-current"} />
                            {loading ? "Architecting..." : "Architect Budget Plan"}
                        </span>
                    </button>
                    <p className="text-center text-xs text-zinc-500 uppercase tracking-widest mt-3 flex justify-center items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Cost: 3 Credits
                    </p>
                </div>
            </div>

            {/* --- RIGHT COLUMN: RESULTS --- */}
            <div className="lg:col-span-8 h-full overflow-hidden flex flex-col" ref={resultsRef}>
                {!budgetPlan ? (
                   <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-black/20 rounded-3xl border border-white/5 backdrop-blur-sm">
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.05)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />
                        
                        <div className="w-32 h-32 bg-zinc-900/80 rounded-full flex items-center justify-center mb-8 border border-zinc-800 shadow-2xl relative z-10">
                            <div className="absolute inset-0 border border-emerald-500/20 rounded-full animate-[ping_3s_infinite]" />
                            <BarChart3 className="w-12 h-12 text-zinc-600"/>
                        </div>
                        <h3 className="text-3xl font-bold text-white font-poppins mb-4 tracking-tight relative z-10">Financial Architect Offline</h3>
                        <p className="text-zinc-500 max-w-md text-sm leading-relaxed relative z-10">
                            Input your total marketing capital to generate a phased allocation strategy based on audience data.
                        </p>
                    </div>
                ) : (
                    <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl flex flex-col h-full overflow-hidden">
                        {/* Header */}
                        <div className="p-6 border-b border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-zinc-900/50">
                            <div>
                                <h2 className="text-xl font-bold text-white font-poppins tracking-tight flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_10px_#10b981] animate-pulse" />
                                    FINANCIAL PLAYBOOK
                                </h2>
                                <p className="text-[10px] text-zinc-400 uppercase tracking-widest pl-4">
                                    Total Allocation: {formatCurrency(parseInt(totalBudget))}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <AppButton onClick={handleExportCSV} variant="secondary" size="sm" icon={FileSpreadsheet} className="text-xs">CSV</AppButton>
                                <AppButton onClick={handleExportPDF} variant="primary" size="sm" icon={FileText} className="text-xs">PDF</AppButton>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-zinc-800 px-6">
                            {[
                                { id: 'snapshot', label: 'Summary', icon: FileText },
                                { id: 'allocation', label: 'Allocation', icon: PieChart },
                                { id: 'timeline', label: 'Cash Flow', icon: Calendar },
                                { id: 'risks', label: 'Risk Map', icon: AlertTriangle }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveReportTab(tab.id)}
                                    className={`flex items-center gap-2 px-4 py-4 text-xs font-bold uppercase tracking-widest border-b-2 transition-colors ${activeReportTab === tab.id ? 'border-emerald-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
                                >
                                    <tab.icon size={14} /> {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Report Content */}
                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-zinc-950/30">
                            {activeReportTab === 'snapshot' && <SnapshotView budgetPlan={budgetPlan} roiAnalysis={roiAnalysis} />}
                            {activeReportTab === 'allocation' && <AllocationView budgetPlan={budgetPlan} formatCurrency={formatCurrency} />}
                            {activeReportTab === 'timeline' && <TimelineView budgetPlan={budgetPlan} />}
                            {activeReportTab === 'risks' && <RiskView budgetPlan={budgetPlan} />}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BudgetPlannerTab;